import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import HomePage from "./components/HomePage";
import LoginPage from "./components/LoginPage";
import RegisterPage from "./components/RegisterPage";
import Dashboard from "./components/Dashboard";
import ChannelPlayer from "./components/ChannelPlayer";
import AdminDashboard from "./components/AdminDashboard";
import AdminUsers from "./components/AdminUsers";
import AdminChannels from "./components/AdminChannels";
import SuperAdminSettings from "./components/SuperAdminSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Settings Context
const SettingsContext = React.createContext();

export const useSettings = () => {
  const context = React.useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

const SettingsProvider = ({ children }) => {
  const [platformSettings, setPlatformSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlatformSettings();
  }, []);

  const fetchPlatformSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/platform`);
      setPlatformSettings(response.data);
      
      // Apply CSS variables for colors
      const root = document.documentElement;
      if (root && response.data) {
        root.style.setProperty('--primary-color', response.data.primary_color || '#3b82f6');
        root.style.setProperty('--secondary-color', response.data.secondary_color || '#8b5cf6');
        root.style.setProperty('--accent-color', response.data.accent_color || '#10b981');
        root.style.setProperty('--background-color', response.data.background_color || '#0f172a');
        root.style.setProperty('--text-color', response.data.text_color || '#ffffff');
      }
      
      // Update page title
      if (response.data?.platform_name) {
        document.title = response.data.platform_name;
      }
      
    } catch (error) {
      console.error('Error fetching platform settings:', error);
      // Set default settings if API fails
      setPlatformSettings({
        platform_name: "Digital TV Play",
        logo_url: null,
        hero_title: "DIGITAL TV PLAY",
        hero_subtitle: "Curta os principais canais",
        hero_description: "com os melhores conteúdos",
        hero_text: "Na Digital TV Play você tem acesso à melhor programação ao vivo com qualidade premium.",
        restriction_warning: "⚠️ Plataforma de Acesso Restrito",
        restriction_text: "O cadastro requer aprovação administrativa para garantir qualidade e segurança.",
        background_image_url: null,
        primary_color: "#3b82f6",
        secondary_color: "#8b5cf6",
        accent_color: "#10b981",
        background_color: "#0f172a",
        text_color: "#ffffff",
        hero_background_color: null,
        footer_text: null
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = (newSettings) => {
    setPlatformSettings(newSettings);
    
    // Apply new CSS variables
    const root = document.documentElement;
    if (root && newSettings) {
      root.style.setProperty('--primary-color', newSettings.primary_color || '#3b82f6');
      root.style.setProperty('--secondary-color', newSettings.secondary_color || '#8b5cf6');
      root.style.setProperty('--accent-color', newSettings.accent_color || '#10b981');
      root.style.setProperty('--background-color', newSettings.background_color || '#0f172a');
      root.style.setProperty('--text-color', newSettings.text_color || '#ffffff');
    }
    
    // Update page title
    if (newSettings?.platform_name) {
      document.title = newSettings.platform_name;
    }
  };

  const value = {
    platformSettings,
    updateSettings,
    refreshSettings: fetchPlatformSettings,
    loading
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Carregando plataforma...</p>
        </div>
      </div>
    );
  }

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

// Auth Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching current user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      toast.success('Login realizado com sucesso!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao fazer login';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (email, password, full_name, phone, address) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { 
        email, 
        password, 
        full_name,
        phone,
        address
      });
      
      toast.success('Cadastro realizado! Aguarde aprovação do administrador.');
      return { success: true, message: response.data.message };
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao criar conta';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logout realizado com sucesso!');
  };

  const value = {
    user,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'superadmin',
    isSuperAdmin: user?.role === 'superadmin',
    isApproved: user?.status === 'approved'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, adminOnly = false, superAdminOnly = false }) => {
  const { isAuthenticated, isAdmin, isSuperAdmin, isApproved, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  if (!isApproved) {
    return <Navigate to="/pending" />;
  }
  
  if (superAdminOnly && !isSuperAdmin) {
    return <Navigate to="/dashboard" />;
  }
  
  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, isApproved, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (isAuthenticated && isApproved) {
    return <Navigate to="/dashboard" />;
  }
  
  return children;
};

const PendingApproval = () => {
  const { user, logout } = useAuth();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        
        <h1 className="text-2xl font-bold text-white mb-4">
          Aguardando Aprovação
        </h1>
        
        <p className="text-gray-300 mb-6">
          Olá <strong>{user?.full_name}</strong>,<br/>
          Sua conta foi criada com sucesso e está aguardando aprovação do administrador.
        </p>
        
        <p className="text-gray-400 text-sm mb-8">
          Você receberá acesso assim que sua conta for aprovada. 
          Entre em contato conosco se tiver dúvidas.
        </p>
        
        <button
          onClick={logout}
          className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
        >
          Sair
        </button>
      </div>
    </div>
  );
};

function App() {
  return (
    <SettingsProvider>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<PublicRoute><HomePage /></PublicRoute>} />
              <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
              <Route path="/pending" element={<PendingApproval />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/channel/:channelId" element={<ProtectedRoute><ChannelPlayer /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
              <Route path="/admin/channels" element={<ProtectedRoute adminOnly><AdminChannels /></ProtectedRoute>} />
              <Route path="/superadmin/settings" element={<ProtectedRoute superAdminOnly><SuperAdminSettings /></ProtectedRoute>} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" />
        </div>
      </AuthProvider>
    </SettingsProvider>
  );
}

export default App;